import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, MessageCircle, RotateCcw } from "lucide-react";
import { waLink } from "@/config/site";

const QUESTIONS = [
  {
    q: "Modern oder natürlich?",
    options: [
      { label: "Modern & klar", score: { modern: 2, minimal: 1 } },
      { label: "Natürlich & wild", score: { natur: 2 } },
    ],
  },
  {
    q: "Viel Grün oder pflegeleicht?",
    options: [
      { label: "Viel Grün", score: { natur: 2 } },
      { label: "Pflegeleicht", score: { minimal: 2 } },
    ],
  },
  {
    q: "Terrasse gewünscht?",
    options: [
      { label: "Ja, großzügig", score: { modern: 2, familie: 1 } },
      { label: "Klein & ruhig", score: { minimal: 1, natur: 1 } },
    ],
  },
  {
    q: "Pflanzen oder klare Linien?",
    options: [
      { label: "Pflanzenvielfalt", score: { natur: 2 } },
      { label: "Klare Linien", score: { modern: 1, minimal: 2 } },
    ],
  },
  {
    q: "Wie groß ist der Garten?",
    options: [
      { label: "Bis 150 m²", score: { minimal: 1, modern: 1 } },
      { label: "150–500 m²", score: { familie: 1, natur: 1 } },
      { label: "Über 500 m²", score: { familie: 2 } },
    ],
  },
];

const RESULTS = {
  modern: {
    title: "Modern",
    text: "Klare Kanten, hochwertige Beläge und ruhige Pflanzflächen. Ihr Garten wirkt als Fortsetzung des Hauses.",
  },
  natur: {
    title: "Natürlich",
    text: "Gräser, Stauden und weiche Übergänge. Ein Garten, der sich mit den Jahreszeiten verändert.",
  },
  minimal: {
    title: "Minimal",
    text: "Wenige Materialien, große Flächen, geringer Pflegeaufwand – reduziert und dadurch großzügig.",
  },
  familie: {
    title: "Familien-Garten",
    text: "Robuster Rasen, offene Flächen und ein Sitzplatz, der Platz für viele bietet.",
  },
};

export const GardenFinder = () => {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState({ modern: 0, natur: 0, minimal: 0, familie: 0 });
  const done = step >= QUESTIONS.length;

  const choose = (opt) => {
    setScore((s) => {
      const next = { ...s };
      Object.entries(opt.score).forEach(([k, v]) => (next[k] += v));
      return next;
    });
    setStep((v) => v + 1);
  };

  const reset = () => {
    setScore({ modern: 0, natur: 0, minimal: 0, familie: 0 });
    setStep(0);
  };

  const winner = Object.entries(score).sort((a, b) => b[1] - a[1])[0][0];
  const result = RESULTS[winner];

  return (
    <div
      data-testid="garden-finder"
      className="relative overflow-hidden rounded-[2.5rem] border border-[#17251C]/12 bg-[#2F3D2E] px-6 py-12 text-[#F3EDE2] md:px-14 md:py-16"
    >
      <svg viewBox="0 0 600 400" className="pointer-events-none absolute -right-20 -top-16 h-72 w-72 opacity-20" aria-hidden="true">
        <circle cx="300" cy="200" r="190" fill="none" stroke="#DCCDB4" strokeWidth="1" />
        <circle cx="300" cy="200" r="120" fill="none" stroke="#DCCDB4" strokeWidth="1" />
      </svg>

      <div className="relative">
        <p className="eyebrow !text-[#DCCDB4]">Inspiration</p>
        <h2 className="display-xl mt-4 max-w-xl text-3xl leading-[0.95] md:text-[3.4rem]">
          Welcher Garten passt zu Ihnen?
        </h2>

        <div className="mt-10 min-h-[220px]">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center gap-4">
                  <span className="text-[0.66rem] uppercase tracking-[0.3em] text-[#93A387]">
                    Frage {step + 1} / {QUESTIONS.length}
                  </span>
                  <span className="h-[1px] flex-1 bg-[#DCCDB4]/20">
                    <motion.span
                      className="block h-full bg-[#B4623C]"
                      animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                      transition={{ duration: 0.6 }}
                    />
                  </span>
                </div>

                <p className="display-xl mt-6 text-2xl md:text-3xl">{QUESTIONS[step].q}</p>

                <div className="mt-7 flex flex-wrap gap-3">
                  {QUESTIONS[step].options.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      data-testid={`finder-option-${step}-${o.label.toLowerCase().replace(/[^a-zäöü0-9]+/g, "-")}`}
                      onClick={() => choose(o)}
                      className="rounded-full border border-[#DCCDB4]/35 px-6 py-3.5 text-sm font-semibold text-[#F3EDE2] transition-all duration-400 hover:-translate-y-1 hover:border-[#DCCDB4] hover:bg-[#DCCDB4]/10"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>

                {step > 0 && (
                  <button
                    type="button"
                    data-testid="finder-back"
                    onClick={() => setStep((v) => v - 1)}
                    className="mt-8 inline-flex items-center gap-2 text-[0.66rem] uppercase tracking-[0.24em] text-[#93A387] transition-colors hover:text-[#F3EDE2]"
                  >
                    <ArrowLeft size={13} /> Zurück
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="result"
                data-testid="finder-result"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="text-[0.66rem] uppercase tracking-[0.3em] text-[#93A387]">Ihre Gartenrichtung</p>
                <p className="display-xl mt-4 text-[13vw] leading-none md:text-[5rem]">{result.title}</p>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-[#DCCDB4]">{result.text}</p>
                <p className="mt-4 max-w-lg text-xs leading-relaxed text-[#93A387]">
                  Hinweis: Dies ist eine Inspirationsfunktion und ersetzt keine Planung. Die konkrete Gestaltung
                  besprechen wir persönlich mit Ihnen.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={waLink(
                      `Hallo Hanse Garten Bremen, ich möchte gerne mein Gartenprojekt besprechen. Mein Ergebnis aus dem Gartenfinder: ${result.title}.`,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="finder-cta-whatsapp"
                    className="btn-primary !bg-[#F3EDE2] !text-[#17251C]"
                  >
                    <MessageCircle size={15} /> Gartenprojekt besprechen
                  </a>
                  <Link to="/kontakt" className="btn-outline on-dark" data-testid="finder-cta-contact">
                    Kostenlose Beratung
                  </Link>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="finder-reset"
                    className="inline-flex items-center gap-2 px-2 text-[0.66rem] uppercase tracking-[0.24em] text-[#93A387] transition-colors hover:text-[#F3EDE2]"
                  >
                    <RotateCcw size={13} /> Neu starten
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
